import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Outlet, Link } from 'react-router-dom';

import AppTopBar from './AppTopBar';
import Sidebar from './Sidebar';
import { Spinner } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { ErrorBanner } from '../../pages/auth/SignInScreen';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import { useBreakpoint } from '../../lib/useBreakpoint';

// ────────────────────────────────────────────────────────────────────────────
// Workspace context — child tab pages access team/role/counts via useWorkspace()
// ────────────────────────────────────────────────────────────────────────────
const WorkspaceContext = createContext(null);

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used inside <WorkspaceShell>');
  return ctx;
}

/**
 * Outer layout for all workspace pages. Loads:
 *   - the team (teams row)
 *   - the current user's membership + role for this team
 *   - all teams the user belongs to (for the switcher)
 *   - sidebar counts (members, templates, documents, pending review)
 *
 * Renders <Outlet> for the active tab page.
 */
export default function WorkspaceShell({ activeTab }) {
  const { teamId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isDesktop, isMobile } = useBreakpoint();

  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [team, setTeam]             = useState(null);
  const [membership, setMembership] = useState(null);   // current user's row in team_members
  const [role, setRole]             = useState(null);   // current user's role
  const [allTeams, setAllTeams]     = useState([]);     // teams for switcher
  const [counts, setCounts]         = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Drawer behavior: body scroll-lock + ESC + browser back-button ────
  useEffect(() => {
    if (!drawerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    const onPop = () => setDrawerOpen(false);

    // Push a sentinel history entry so back-button closes the drawer
    // instead of navigating away from the page.
    window.history.pushState({ miDrawer: true }, '');
    window.addEventListener('keydown', onKey);
    window.addEventListener('popstate', onPop);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
      // If the sentinel is still on top of history (drawer closed by tap/ESC/nav),
      // pop it so we don't pollute history. If user pressed back, it's already gone.
      if (window.history.state?.miDrawer) window.history.back();
    };
  }, [drawerOpen]);

  // Close drawer automatically when viewport grows past tablet
  // (e.g. user rotates iPad from portrait to landscape).
  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  // ── Loaders ────────────────────────────────────────────────────────────
  const loadCounts = useCallback(async (tid) => {
    const queries = [
      supabase.from('team_members')      .select('id', { count: 'exact', head: true }).eq('team_id', tid),
      supabase.from('document_templates').select('id', { count: 'exact', head: true }).eq('team_id', tid),
      supabase.from('document_uploads')  .select('id', { count: 'exact', head: true }).eq('team_id', tid),
      supabase.from('document_uploads')  .select('id', { count: 'exact', head: true }).eq('team_id', tid).eq('status', 'in_review'),
    ];
    const [m, t, d, p] = await Promise.all(queries);
    return {
      members:   m.count || 0,
      templates: t.count || 0,
      documents: d.count || 0,
      pending:   p.count || 0,
    };
  }, []);

  const refreshCounts = useCallback(async () => {
    if (!teamId) return;
    try {
      const c = await loadCounts(teamId);
      setCounts(c);
    } catch (err) {
      console.warn('refreshCounts failed', err);
    }
  }, [teamId, loadCounts]);

  // Load everything when teamId or user changes
  useEffect(() => {
    if (!user || !teamId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 1) Load this team
        const { data: teamData, error: teamErr } = await supabase
          .from('teams')
          .select('*')
          .eq('id', teamId)
          .single();
        if (teamErr) throw teamErr;
        if (cancelled) return;
        setTeam(teamData);

        // 2) Load current user's membership + role
        const { data: memData, error: memErr } = await supabase
          .from('team_members')
          .select('id, team_id, user_id, role_id, is_owner, email, first_name, last_name, team_roles(*)')
          .eq('team_id', teamId)
          .eq('user_id', user.id)
          .maybeSingle();
        if (memErr) throw memErr;
        if (!memData) throw new Error('You don\'t have access to this workspace.');
        if (cancelled) return;
        setMembership(memData);
        setRole(memData.team_roles);

        // 3) Load all teams for switcher (parallel, non-blocking)
        supabase
          .from('team_members')
          .select('teams(id, name, slug, plan, primary_color)')
          .eq('user_id', user.id)
          .then(({ data }) => {
            if (cancelled) return;
            const ts = (data || []).map(r => r.teams).filter(Boolean);
            setAllTeams(ts);
          });

        // 4) Load sidebar counts
        const c = await loadCounts(teamId);
        if (cancelled) return;
        setCounts(c);
      } catch (err) {
        console.error('Workspace load failed:', err);
        if (!cancelled) setError(err.message || 'Failed to load workspace.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [teamId, user, loadCounts]);

  // ── Loading + error states ────────────────────────────────────────────
  if (loading && !team) {
    return (
      <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
        <AppTopBar />
        <div style={{
          height: 'calc(100vh - 56px)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', gap: 12,
          color: 'var(--text-3)', fontSize: 13,
        }}>
          <Spinner size={18} color="var(--accent)" />
          Loading workspace…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
        <AppTopBar />
        <div style={{
          maxWidth: 520, margin: '60px auto', padding: 24,
        }}>
          <ErrorBanner message={error} />
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Link to="/hub" style={{
              fontSize: 13, color: 'var(--accent-600)', fontWeight: 600,
              textDecoration: 'none',
            }}>
              ← Back to Hub
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────
  const ctxValue = {
    team,
    teamId: team?.id,
    membership,
    role,
    counts,
    refreshCounts,
    refresh: refreshCounts,  // alias for panels that just call refresh()
    allTeams,
    isOwner: !!membership?.is_owner,
    isStaff: !!role?.is_staff,
  };

  // Breadcrumb: on mobile, drop "Workspaces / " (drawer + logo handle that)
  // and just show the team name so the topbar isn't crammed.
  const breadcrumb = isMobile ? (
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {team?.name}
    </span>
  ) : (
    <>
      <span
        style={{ color: 'var(--text-3)', cursor: 'pointer' }}
        onClick={() => navigate('/hub')}
      >
        Workspaces
      </span>
      <Icon.chevR size={12} stroke="var(--text-muted)" />
      <span>{team?.name}</span>
    </>
  );

  const sidebarProps = {
    team,
    memberCount: counts.members,
    active: activeTab,
    counts,
    allTeams,
    isStaff: !!role?.is_staff,
    isOwner: !!membership?.is_owner,
  };

  return (
    <WorkspaceContext.Provider value={ctxValue}>
      <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
        <AppTopBar
          notificationCount={counts.pending || 0}
          breadcrumb={breadcrumb}
          onMenuClick={isDesktop ? undefined : () => setDrawerOpen(true)}
        />
        <div style={{ display: 'flex', minHeight: 'calc(100vh - 56px)' }}>
          {/* Desktop: sidebar inline; mobile/tablet: in drawer below */}
          {isDesktop && <Sidebar {...sidebarProps} />}
          <main style={{
            flex: 1, minWidth: 0,
            padding: isMobile ? '16px' : '28px 32px 48px',
          }}>
            <div style={{ maxWidth: 1280, margin: '0 auto' }}>
              <Outlet />
            </div>
          </main>
        </div>

        {/* Mobile/tablet drawer */}
        {!isDesktop && (
          <>
            {/* Overlay */}
            <div
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
              style={{
                position: 'fixed', inset: 0, zIndex: 60,
                background: 'rgba(10,15,46,0.45)',
                opacity: drawerOpen ? 1 : 0,
                pointerEvents: drawerOpen ? 'auto' : 'none',
                transition: 'opacity 0.2s ease',
              }}
            />
            {/* Drawer panel */}
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Workspace navigation"
              aria-hidden={drawerOpen ? 'false' : 'true'}
              style={{
                position: 'fixed', top: 0, left: 0, bottom: 0,
                width: 280, maxWidth: '85vw', zIndex: 70,
                background: 'var(--surface)',
                boxShadow: drawerOpen ? 'var(--shadow-xl)' : 'none',
                // -105% buffer so subpixel rendering / border-right can't leak
                // a sliver at the left viewport edge when closed.
                transform: drawerOpen ? 'translateX(0)' : 'translateX(-105%)',
                visibility: drawerOpen ? 'visible' : 'hidden',
                pointerEvents: drawerOpen ? 'auto' : 'none',
                transition: drawerOpen
                  ? 'transform 0.22s ease, visibility 0s'
                  : 'transform 0.22s ease, visibility 0s 0.22s',
                display: 'flex', flexDirection: 'column',
                willChange: 'transform',
              }}
            >
              <Sidebar
                {...sidebarProps}
                variant="drawer"
                onNavigate={() => setDrawerOpen(false)}
              />
            </div>
          </>
        )}
      </div>
    </WorkspaceContext.Provider>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Reusable PageHeader for tab pages
// ────────────────────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'flex-end',
      justifyContent: 'space-between',
      marginBottom: isMobile ? 14 : 24,
      paddingTop: isMobile ? 0 : 4,
      gap: isMobile ? 10 : 16,
      flexWrap: 'wrap',
    }}>
      <div style={{ minWidth: 0, flex: isMobile ? '1 1 100%' : 'initial' }}>
        <h1 style={{
          fontSize: isMobile ? 20 : 26, fontWeight: 700, letterSpacing: '-0.025em',
          marginBottom: isMobile ? 2 : 4, color: 'var(--text)', lineHeight: 1.2,
        }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ fontSize: isMobile ? 12.5 : 13.5, color: 'var(--text-3)' }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && (
        <div style={{
          display: 'flex', gap: 8,
          width: isMobile ? '100%' : undefined,
        }}>
          {actions}
        </div>
      )}
    </div>
  );
}
